import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { AlarmClock, X } from 'lucide-react'
import { useTaskReminderCycle } from '@/modules/tasks/hooks/useTaskReminderCycle'
import {
  formatReminderCount,
  REMINDER_MOBILE_FIRST_DELAY_MS,
  REMINDER_MOBILE_LOOPS_PER_BURST,
  tasksReminderHref,
} from '@/modules/tasks/utils/task-reminder'
import { usePermission } from '@/hooks/useAuth'
import { usePrefersReducedMotion } from '@/hooks/useMediaQuery'
import { SwipeToDelete } from '@/components/common/SwipeToDelete'
import type { ModuleId } from '@/constants/modules'
import { cn } from '@/utils/cn'

interface TaskReminderFabProps {
  /** Hidden inside Tasks — you are already looking at the list there. */
  currentModuleId?: ModuleId
}

/**
 * Mobile reminder banner — fixed under the app bar, elevated above page chrome
 * so shadow and corners aren’t clipped by toolbars / cards below.
 * One burst per browser session (5 min delay, single loop); not on refresh / module change.
 */
export function TaskReminderFab({ currentModuleId }: TaskReminderFabProps) {
  const navigate = useNavigate()
  const reducedMotion = usePrefersReducedMotion()
  const inTasksModule = currentModuleId === 'tasks'
  const canViewTasks = usePermission('tasks.view')
  // Keep the cycle enabled across module changes — only hide UI inside Tasks.
  const { current, attention, dismiss } = useTaskReminderCycle(canViewTasks, {
    firstDelayMs: REMINDER_MOBILE_FIRST_DELAY_MS,
    loopsPerBurst: REMINDER_MOBILE_LOOPS_PER_BURST,
    oncePerSession: true,
  })

  if (!canViewTasks || inTasksModule) return null

  const isOverdue = current?.kind === 'overdue'
  const tween = reducedMotion
    ? { duration: 0.12 }
    : { duration: 0.34, ease: [0.16, 1, 0.3, 1] as const }

  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-[45] flex justify-center px-3"
      style={{
        top: 'calc(3.5rem + env(safe-area-inset-top, 0px) + 0.5rem)',
      }}
      aria-live="polite"
    >
      <AnimatePresence mode="wait" initial={false}>
        {attention && current ? (
          <motion.div
            key={current.kind}
            initial={
              reducedMotion
                ? { opacity: 0 }
                : { opacity: 0, y: -14, scale: 0.97 }
            }
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={
              reducedMotion
                ? { opacity: 0 }
                : { opacity: 0, y: -10, scale: 0.98 }
            }
            transition={tween}
            className="pointer-events-auto w-full max-w-lg"
          >
            <SwipeToDelete
              onDelete={dismiss}
              revealAffordance={false}
              immediate
              surfaceClassName={
                isOverdue ? 'rounded-2xl bg-destructive/95' : 'rounded-2xl bg-primary/95'
              }
              className="w-full rounded-2xl shadow-float"
            >
              <div
                className={cn(
                  'flex w-full items-center gap-2 rounded-2xl px-3 py-2.5 text-white backdrop-blur-sm',
                  'border',
                  isOverdue ? 'border-destructive/40' : 'border-primary/40',
                )}
              >
                <button
                  type="button"
                  onClick={() => navigate(tasksReminderHref(current.kind))}
                  aria-label={`${current.label}, ${current.count} tasks`}
                  className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/20">
                    <AlarmClock className="h-4 w-4" aria-hidden />
                  </span>

                  <span className="min-w-0 flex-1 overflow-hidden">
                    <span className="block truncate text-[13px] font-semibold leading-tight tracking-tight">
                      {current.label}
                    </span>
                    <span className="mt-0.5 block truncate text-[11px] font-medium leading-none text-white/80">
                      {current.count === 1
                        ? '1 task needs attention'
                        : `${formatReminderCount(current.count)} tasks need attention`}
                    </span>
                  </span>

                  <span className="flex h-7 min-w-7 shrink-0 items-center justify-center rounded-full bg-white/25 px-2 text-[13px] font-bold tabular-nums">
                    {formatReminderCount(current.count)}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation()
                    dismiss()
                  }}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-white/85 transition-colors hover:bg-white/15"
                  aria-label={`Dismiss ${current.label}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </SwipeToDelete>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  )
}
